/**
 * historySectionRegistry
 *
 * Maps history sub-section renderer component keys → { Component, propBuilder }.
 *
 * Data paths are read from subSection.dataPath (config-driven) rather than
 * being hardcoded here, matching the same pattern used in sectionRegistry.js.
 *
 * subSection config shape (from defaultDoctorConsole.config.json):
 *   { key, dataStore, dataPath, renderer: { component } }
 *
 * The runtime object passed to each prop builder:
 *   { medicalBackground, updateMedicalBackground, selectedSectionsData,
 *     updateSectionData, commonProps, dateForAppointment, config, ruleContext,
 *     subSection }
 */

import React from "react";
import FamilyHistory from "../../HistoriesData/FamilyHistory";
import AllergyIntolerance from "../../HistoriesData/AllergyIntolerance";
import AddictionHistory from "../../HistoriesData/AddictionHistory";
import DrugHistory from "../../HistoriesData/DrugHistory";
import MenstrualHistory from "../../HistoriesData/MenstrualHistory";
import MedicalHistory from "../../HistoriesData/MedicalHistory";
import SurgicalHistory from "../../HistoriesData/SurgicalHistory";
import AdultVaccinationHistory from "../../HistoriesData/AdultVaccinationHistory";
import InitialObservation from "../../HistoriesData/InitialObservation";

import { resolveSectionFieldLayout } from "../engine/resolveSectionFields";

// ─── component map ────────────────────────────────────────────────────────────

const historyComponentMap = {
  FamilyHistory,
  AllergyIntolerance,
  AddictionHistory,
  DrugHistory,
  MenstrualHistory,
  MedicalHistory,
  SurgicalHistory,
  AdultVaccinationHistory,
  InitialObservation,
};

// ─── prop builders — data paths come from subSection.dataPath (config) ────────

const historyPropBuilders = {
  /**
   * Most medicalBackground-based sections follow the same pattern:
   * read/write medicalBackground[subSection.dataPath].
   * The prop names are specific to each component's interface.
   */

  FamilyHistory: ({ medicalBackground, updateMedicalBackground, commonProps, subSection }) => ({
    familyHistoryData: medicalBackground[subSection.dataPath],
    setFamilyHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    ...commonProps,
  }),

  MedicalHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
    dateForAppointment,
  }) => ({
    medicalHistoryData: medicalBackground[subSection.dataPath],
    setMedicalHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    dateForAppointment,
    sectionName: subSection.renderer?.props?.sectionName ?? subSection.key,
    ...commonProps,
  }),

  // InitialObservation lives in selectedSectionsData, not medicalBackground
  InitialObservation: ({
    selectedSectionsData,
    updateSectionData,
    commonProps,
    subSection,
  }) => ({
    data: selectedSectionsData[subSection.dataPath],
    setData: (data) => updateSectionData(subSection.dataPath, data),
    ...commonProps,
  }),

  SurgicalHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
    dateForAppointment,
  }) => ({
    surgicalHistoryData: medicalBackground[subSection.dataPath],
    setSurgicalHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    dateForAppointment,
    ...commonProps,
  }),

  AllergyIntolerance: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
  }) => ({
    allergyHistoryData: medicalBackground[subSection.dataPath],
    setAllergyHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    ...commonProps,
  }),

  AddictionHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
    dateForAppointment,
  }) => ({
    addictionHistoryData: medicalBackground[subSection.dataPath],
    setAddictionHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    dateForAppointment,
    ...commonProps,
  }),

  DrugHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
    dateForAppointment,
  }) => ({
    drugHistoryData: medicalBackground[subSection.dataPath],
    setDrugHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    dateForAppointment,
    ...commonProps,
  }),

  MenstrualHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
    dateForAppointment,
  }) => ({
    menstrualHistoryData: medicalBackground[subSection.dataPath],
    setMenstrualHistoryData: (data) => updateMedicalBackground(subSection.dataPath, data),
    dateForAppointment,
    ...commonProps,
  }),

  AdultVaccinationHistory: ({
    medicalBackground,
    updateMedicalBackground,
    commonProps,
    subSection,
  }) => ({
    adultVaccinationHistoryData: medicalBackground[subSection.dataPath],
    setAdultVaccinationHistoryData: (data) =>
      updateMedicalBackground(subSection.dataPath, data),
    ...commonProps,
  }),
};

// ─── public renderer ──────────────────────────────────────────────────────────

export const renderHistorySubSection = (subSection, runtime) => {
  const componentName = subSection.renderer?.component;
  const Component = historyComponentMap[componentName];
  const buildProps = historyPropBuilders[componentName];

  if (!Component || !buildProps) return null;

  const fieldLayout = subSection.fieldSet
    ? resolveSectionFieldLayout(subSection, runtime.config, runtime.ruleContext)
    : null;

  return (
    <Component
      {...buildProps({ ...runtime, subSection })}
      fieldLayout={fieldLayout}
    />
  );
};
